import { activeNavItemAtom } from '@/global-store';
import { useAtom } from 'jotai';
import type { KeyboardEvent } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import SidebarNavButton from '@layout/Sidebar/MenuList/SidebarNavButton';
import NavItemLabel from '@layout/Sidebar/MenuList/NavItemLabel';
import { useBadgeVisibility } from '@layout/Sidebar/MenuList/useBadgeVisibility';
import { useSidebar } from '@layout/Sidebar/SidebarContext';
import type { MenuItem } from '@layout/Sidebar/MenuList/types';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T, type TProps } from '@/i18n/T';

interface NavItemProps {
  item: MenuItem;
  level?: number;
  onEscapeCollapse?: () => void;
}

function NavItem({ item, level = 0, onEscapeCollapse }: NavItemProps) {
  const { t, text } = useInlineTranslation();
  const [, setActiveNavItem] = useAtom(activeNavItemAtom);
  const { isMobile, setOpenMobile } = useSidebar();
  const showBadge = useBadgeVisibility(item.handle?.badge);
  const { pathname, search } = useLocation();

  const selected = isItemActive(item, pathname, search);
  const { badgeText, title } = getUIStrings(item, T);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || !onEscapeCollapse) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onEscapeCollapse?.();
  };

  return (
    <SidebarNavButton
      component={Link}
      target={item.target ? '_blank' : '_self'}
      to={item.path}
      level={level}
      selected={selected}
      aria-label={
        text(title) + (showBadge && badgeText ? ` - ${badgeText}` : '')
      }
      role='link'
      aria-current={selected ? 'page' : undefined}
      disabled={item.disabled}
      onClick={() => {
        setActiveNavItem(item.path);
        if (isMobile) {
          setOpenMobile(false);
        }
      }}
      onKeyDown={handleKeyDown}
      tooltip={
        item.handle?.i18nTooltipKey ? t(item.handle.i18nTooltipKey) : undefined
      }
    >
      <NavItemLabel item={item} title={title} />
    </SidebarNavButton>
  );
}

function isItemActive(
  item: MenuItem,
  pathname: string,
  search: string,
): boolean {
  const {
    activeOnPaths = [],
    inactiveOnPaths = [],
    activeOnQueryIncludes = [],
    inactiveOnQueryIncludes = [],
  } = item.handle ?? {};

  const matchesExactPath = (path: string) =>
    Boolean(matchPath({ path, end: true }, pathname));

  const matchesPathPrefix = (path: string) =>
    Boolean(matchPath({ path, end: false }, pathname));

  const queryForcesInactive = inactiveOnQueryIncludes?.some((part) =>
    search.includes(part),
  );

  if (queryForcesInactive) {
    return false;
  }

  const queryForcesActive = activeOnQueryIncludes?.some((part) =>
    search.includes(part),
  );

  if (queryForcesActive) {
    return true;
  }

  const matchesActivePath =
    matchesExactPath(item.path) || activeOnPaths?.some(matchesPathPrefix);

  const matchesInactivePath = inactiveOnPaths?.some(matchesPathPrefix);

  return matchesActivePath && !matchesInactivePath;
}

function getUIStrings(item: MenuItem, TComp: React.ComponentType<TProps>) {
  const badgeText =
    typeof item.handle?.badge?.text === 'string' ? item.handle.badge.text : '';

  const title = item.handle?.i18nNameKey ? (
    <TComp k={item.handle.i18nNameKey} />
  ) : (
    (item.handle?.name ?? '')
  );

  return { badgeText, title };
}

export default NavItem;
